import React from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import classNames from 'classnames'
import { tv } from 'tailwind-variants'
import { formatUSDC } from '@/util/currencyHelpers'
import { designSystem } from '@/util/tailwindHelpers'
import Icon from '../Icon'
import Toast from '../Toast'
import PoweredBySolana from '../PoweredBySolana'
import { Web3WalletStatus } from '@/features/wallet/types'
import ConnectedStatusIcon from '@/features/wallet/components/ConnectedStatusIcon'

const X_PAD = 24
const CARD_WIDTH = 343
const CARD_HEIGHT = 212
const DEFAULT_TEXT_FILL = 'white'

const STATUS_LABELS = {
  disconnected: 'Disconnected',
  connected: 'Connected'
}

const BORDER_STROKE = {
  disconnected: 'url(#defaultStrokeGradient)',
  connected: 'url(#solanaStrokeGradient)'
}

const BG_FILL = {
  disconnected: designSystem.colors['gray-800'],
  connected: 'url(#solanaBgGradient)'
}

type Props = {
  className?: string
  status: Exclude<Web3WalletStatus, 'none'>
  account: {
    address: string
    display_address: string
  }
  balance: number
}

const blurClasses = tv({
  variants: {
    status: {
      disconnected: '',
      connected:
        'relative after:content-[""] after:absolute after:-inset-[0px] after:bg-solana--circle after:blur-[10px] after:rounded-[10%] after:opacity-90'
    }
  }
})

function DropppWalletCard({ className, balance = 0, account, status }: Props) {
  const borderStroke = BORDER_STROKE[status]
  const bgFill = BG_FILL[status]

  const statusLabel = STATUS_LABELS[status]
  const priceString = formatUSDC(balance, { includeUSDCSuffix: false })

  return (
    <div className={classNames(className, blurClasses({ status }))}>
      <svg
        className="leading-none w-full h-full relative z-10 "
        width={CARD_WIDTH}
        height={CARD_HEIGHT}
        viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}
      >
        <defs>
          <linearGradient
            id="solanaBgGradient"
            gradientTransform="rotate(-55,.5,.5)"
          >
            <stop offset="0" stopColor="#BD3FF2" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#33CBB8" stopOpacity={0.4} />
          </linearGradient>

          <linearGradient
            id="solanaStrokeGradient"
            gradientTransform="rotate(-20,.5,.5)"
          >
            <stop offset="0" stopColor="#BD3FF2" />
            <stop offset="100%" stopColor="#33CBB8" />
          </linearGradient>

          <linearGradient
            id="defaultStrokeGradient"
            gradientTransform="rotate(-55,.5,.5)"
          >
            <stop offset="0" stopColor="#707070" />
            <stop offset="0.5" stopColor="#D6D6D6" />
            <stop offset="1" stopColor="#707070" />
          </linearGradient>
        </defs>

        {/* Container */}
        <rect
          x="1"
          y="1"
          width={CARD_WIDTH - 2}
          height={CARD_HEIGHT - 2}
          fill={designSystem.colors['gray-900']}
          rx="24"
        />
        <rect
          x="1"
          y="1"
          width={CARD_WIDTH - 2}
          height={CARD_HEIGHT - 2}
          fill={bgFill}
          stroke={borderStroke}
          rx="24"
          strokeWidth="1"
        />

        {/* BG LOGO */}
        <g>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="237"
            height="197"
            fill="none"
            y={16}
            opacity={0.1}
          >
            <path
              fill="url(#paint0_linear_22588_26558)"
              d="M149.548 82.812c.988 0 1.98.027 2.971.077l-38.317 169.999-.228.924c-1.755 6.721-5.931 12.435-11.842 16.166-6.174 3.894-13.494 5.155-20.62 3.533-14.67-3.333-23.905-17.982-20.582-32.656l23.921-105.704.266-1.127c7.437-30.358 34.688-51.212 64.431-51.212Zm24.533 4.867c12.435 5.06 22.991 13.775 30.395 25.317 9.587 14.946 12.703 32.712 8.771 50.023-7.193 31.659-36.266 52.766-67.652 51.033l28.486-126.373ZM55.886 162.961c-7.675 31.374-36.513 52.605-67.408 51.156L26.8 44.115l.225-.924c1.755-6.72 5.934-12.438 11.839-16.166 6.177-3.897 13.5-5.151 20.623-3.533 14.67 3.333 23.905 17.982 20.585 32.656L56.15 161.849l-.263 1.112Zm-88.967 46.36-1.325-.556c-11.857-5.127-21.932-13.633-29.07-24.762-9.587-14.942-12.703-32.708-8.771-50.019 7.19-31.659 36.263-52.766 67.652-51.033L-33.08 209.32Zm134.904-149.27c5.39-26.215-11.23-52.144-37.446-58.099-12.883-2.925-26.13-.655-37.298 6.393C15.908 15.397 8.159 26.374 5.255 39.26L.316 61.16l-1.3-.117c-42.833-3.561-83.006 24.938-92.8 68.048-5.254 23.131-1.093 46.87 11.718 66.841 12.808 19.964 32.659 33.644 55.897 38.514a85.37 85.37 0 0 0 17.571 1.826c18.841 0 36.928-6.236 51.787-17.102l-3.802 16.808-.21.97c-5.39 26.216 11.23 52.148 37.446 58.103a49.894 49.894 0 0 0 11.036 1.242c9.22 0 18.272-2.598 26.262-7.639 11.168-7.048 18.921-18.028 21.824-30.91l4.939-21.905 1.3.121c42.858 3.561 83.009-24.938 92.8-68.048 5.254-23.134 1.094-46.873-11.718-66.841-12.808-19.964-32.659-33.644-55.897-38.517a85.407 85.407 0 0 0-23.598-1.613c-16.656 1.18-32.496 7.2-45.766 16.91l3.808-16.83.21-.97ZM173.8 6.214c1.463-3.86 5.506-6.076 9.528-5.222 4.023.853 6.827 4.522 6.609 8.646-.217 4.125-3.391 7.477-7.481 7.902l-.111.741c-1.783 12.085-2.408 23.013 2.915 24.936l.192.05c5.406 1.785 11.959-7.42 18.322-18.093a8.401 8.401 0 0 1-.579-10.869 8.31 8.31 0 0 1 10.598-2.264c3.605 1.97 5.246 6.283 3.867 10.167-1.379 3.884-5.366 6.183-9.399 5.418l-.292.671c-4.753 10.984-8.419 22.826-3.958 24.436 3.519 1.266 10.443-3.42 17.983-9.802a7.714 7.714 0 0 1 1.509-8.267 7.628 7.628 0 0 1 8.14-1.941l.009-.004a7.675 7.675 0 0 1 5.017 5.869 7.708 7.708 0 0 1-2.607 7.281 7.626 7.626 0 0 1-7.59 1.317 7.504 7.504 0 0 1-.951-.419l-17.659 21.16c-11.25-8.2-24.222-13.997-38.267-16.942a97.357 97.357 0 0 0-17.386-2.043l-.204-28.682a8.238 8.238 0 0 1-.873-.26c-3.415-1.232-5.498-4.704-4.989-8.313.51-3.61 3.473-6.363 7.094-6.594 3.621-.231 6.905 2.124 7.863 5.64.957 3.516-.675 7.224-3.908 8.88l.161.868c1.756 9.3 4.085 16.851 7.464 18.074 4.552 1.643 9.332-10.339 12.64-22.076-3.569-2.05-5.121-6.404-3.657-10.265Z"
            />
            <defs>
              <linearGradient
                id="paint0_linear_22588_26558"
                x1="70.5"
                x2="70.5"
                y1=".708"
                y2="296.293"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#fff" />
                <stop offset="1" stopColor="#fff" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </g>

        {/* Logo Mark */}
        <g
          transform={`translate(${CARD_WIDTH - X_PAD - 32},${X_PAD})`}
          className="w-2"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 37 38"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M26.5826 11.8887C26.6896 11.8887 26.7969 11.8917 26.9043 11.897L22.7561 30.3009L22.7314 30.4009C22.5415 31.1285 22.0894 31.747 21.4494 32.151C20.781 32.5726 19.9886 32.709 19.2172 32.5335C17.6289 32.1727 16.6292 30.5868 16.989 28.9982L19.5786 17.5549L19.6074 17.4328C20.4125 14.1463 23.3627 11.8887 26.5826 11.8887ZM29.2385 12.4157C30.5846 12.9633 31.7275 13.9069 32.529 15.1565C33.5669 16.7745 33.9042 18.6977 33.4786 20.5719C32.6998 23.9991 29.5524 26.2842 26.1546 26.0966L29.2385 12.4157ZM16.4429 20.5655C15.612 23.962 12.4901 26.2604 9.1454 26.1036L13.2942 7.69941L13.3186 7.59944C13.5085 6.87186 13.9609 6.25294 14.6003 5.84936C15.269 5.42739 16.0618 5.29164 16.8328 5.46685C18.4211 5.82763 19.4208 7.41353 19.0614 9.0021L16.4714 20.4451L16.4429 20.5655ZM6.81152 25.5843L6.66807 25.5242C5.38444 24.9691 4.29374 24.0483 3.52102 22.8435C2.48314 21.2259 2.14577 19.3026 2.57142 17.4285C3.34982 14.0012 6.49721 11.7162 9.89538 11.9037L6.81152 25.5843ZM21.416 9.42474C21.9994 6.58664 20.2002 3.77964 17.3621 3.13498C15.9675 2.81833 14.5334 3.06409 13.3243 3.82712C12.1149 4.59047 11.276 5.77881 10.9617 7.17379L10.427 9.54478L10.2863 9.53207C5.64926 9.14655 1.30016 12.2317 0.239881 16.8988C-0.328877 19.4029 0.121515 21.9728 1.50847 24.1349C2.89508 26.2962 5.04406 27.7771 7.55983 28.3044C8.1968 28.4382 8.8331 28.502 9.46204 28.502C11.5017 28.502 13.4597 27.8269 15.0684 26.6506L14.6568 28.4703L14.634 28.5752C14.0506 31.4133 15.8498 34.2207 18.6879 34.8653C19.0848 34.9553 19.485 34.9998 19.8826 34.9998C20.8807 34.9998 21.8607 34.7186 22.7257 34.1729C23.9348 33.4099 24.774 32.2212 25.0883 30.8265L25.623 28.4552L25.7637 28.4683C30.4034 28.8538 34.7502 25.7686 35.8101 21.1015C36.3789 18.5971 35.9285 16.0271 34.5415 13.8655C33.1549 11.7041 31.0059 10.2232 28.4902 9.69558C27.6323 9.51564 26.7744 9.46166 25.9356 9.52107C24.1324 9.6488 22.4175 10.3006 20.981 11.3517L21.3932 9.52973L21.416 9.42474ZM29.208 3.5963C29.3665 3.1784 29.8042 2.93851 30.2396 3.0309C30.6751 3.12328 30.9786 3.52044 30.9551 3.96697C30.9315 4.41349 30.5879 4.77642 30.1452 4.82237L30.1332 4.90262C29.9402 6.21093 29.8725 7.39396 30.4487 7.60211L30.4695 7.60764C31.0548 7.80076 31.7642 6.80431 32.453 5.64885C32.1466 5.32571 32.12 4.82651 32.3903 4.47219C32.6606 4.11788 33.1474 4.0139 33.5377 4.22711C33.9279 4.44033 34.1055 4.90727 33.9563 5.32773C33.807 5.7482 33.3753 5.99705 32.9388 5.91429L32.9072 5.98696C32.3926 7.17605 31.9958 8.45798 32.4786 8.63233C32.8597 8.76942 33.6093 8.26217 34.4255 7.57118C34.2984 7.26677 34.3625 6.9156 34.5888 6.67616C34.8151 6.43672 35.1609 6.35428 35.4701 6.46604L35.471 6.4656C35.7527 6.56371 35.9602 6.80638 36.0142 7.10097C36.0682 7.39557 35.9603 7.69663 35.7319 7.88925C35.5035 8.08187 35.1897 8.13631 34.9103 8.03181C34.8749 8.0191 34.8405 8.00395 34.8073 7.98648L32.8956 10.2772C31.6776 9.38941 30.2734 8.76196 28.7529 8.44306C28.1294 8.31229 27.5001 8.23881 26.8706 8.22194L26.8486 5.11689C26.8166 5.10922 26.785 5.0998 26.7541 5.08865C26.3844 4.95534 26.1589 4.57949 26.214 4.18876C26.2692 3.79803 26.5899 3.49986 26.9819 3.47485C27.374 3.44985 27.7295 3.70488 27.8331 4.08548C27.9368 4.46608 27.7601 4.86759 27.4101 5.0468L27.4276 5.14075C27.6177 6.14754 27.8698 6.96509 28.2356 7.09745C28.7284 7.27534 29.2459 5.97815 29.6039 4.70754C29.2176 4.48568 29.0496 4.0142 29.208 3.5963Z"
              fill="white"
            />
          </svg>
        </g>

        {/* Card Label */}
        <g transform={`translate(${X_PAD},34)`}>
          <text
            fill={DEFAULT_TEXT_FILL}
            fontSize="16"
            fontWeight={900}
            dominantBaseline="middle"
            className="uppercase"
          >
            Droppp Wallet
          </text>
          <foreignObject y="12" width={CARD_WIDTH} height="56">
            <PoweredBySolana
              icon="solanaWordmarkWhite"
              className="text-xxs text-gray-300 flex gap-half items-center"
            />
          </foreignObject>
        </g>

        {/* Balance */}
        <g transform={`translate(${X_PAD},132)`}>
          <text fill={DEFAULT_TEXT_FILL} fontSize="30" fontWeight={900}>
            {priceString}
            <tspan
              fontSize={12}
              dx={6}
              fontWeight={600}
              fill={`rgba(255,255,255,0.64)`}
            >
              USDC
            </tspan>
          </text>
        </g>

        <g transform={`translate(${X_PAD}, ${CARD_HEIGHT - X_PAD - 36})`}>
          <rect
            width={CARD_WIDTH - X_PAD * 2}
            height={36}
            rx={12}
            fill="white"
            opacity={0.15}
          />
          <foreignObject
            x="12"
            y="0"
            width={CARD_WIDTH - X_PAD * 2 - 24}
            height="36"
          >
            <div className="h-full w-full flex items-center justify-between body-sm">
              <div className="flex items-center gap-half">
                <span>{statusLabel}</span>
                <ConnectedStatusIcon
                  connected={status === 'connected'}
                  className="mt-[1px]"
                />
              </div>
              <div className="flex gap-half items center cursor-pointer">
                <CopyToClipboard
                  text={account.address}
                  onCopy={() => {
                    Toast({
                      type: 'success',
                      description: 'Copied to clipboard.'
                    })
                  }}
                >
                  <div className="flex items-center gap-half">
                    <span>{account.display_address}</span>
                    <Icon name="copy" size={14} />
                  </div>
                </CopyToClipboard>
              </div>
            </div>
          </foreignObject>
        </g>
      </svg>
    </div>
  )
}

export default DropppWalletCard
